/**
 * Row shape for addressbooks loaded into the Addressbooks table.
 * Index signature supports dynamic column updates from react-table.
 */
export interface AddressbookRow {
  id?: string | number;
  accountId?: string | number;
  accountName?: string;
  name?: string;
  key?: string;
  partyIdDirty?: boolean;
  partyIdValid?: boolean;
  party_id?: number | string | null;
  party_name?: string | null;
  haspartyid_processed_accountid_addressbookid?: string;
  haspartyid_processed_addressbookid?: string;
  haspartyid_senttobq_accountid_addressbookid?: string;
  communityLeaderId?: string | null;
  communityLeaderName?: string | null;
  communityLeaderMappingValid?: boolean | null;
  nonConsented?: boolean;
  dataCapture?: boolean;
  deletedFromDD?: boolean;
  category?: string;
  contacts_count?: number;
  canopusId?: string | null;
  canopusName?: string;
  canopusValid?: boolean | null;
  custom_canopus?: boolean | null;
  modified?: string;
  mappingDDAccountId?: string | number;
  tag?: string;
  [field: string]: unknown;
}

export type AddressbookTableRow = AddressbookRow;

const rowKey = (
  value: AddressbookTableRow | undefined,
  fallbackKey: string
): string => {
  if (value?.key) {
    return String(value.key);
  }
  if (value?.accountId != null && value?.id != null) {
    return `${value.accountId}_${value.id}`;
  }
  return fallbackKey;
};

const parseModified = (modified: string | undefined): number | null => {
  if (!modified) {
    return null;
  }
  const parsed = Date.parse(modified);
  return Number.isNaN(parsed) ? null : parsed;
};

export const isLocalAddressbookRowNewer = (
  existing: AddressbookTableRow,
  incoming: AddressbookTableRow
): boolean => {
  const existingModified = parseModified(
    typeof existing.modified === 'string' ? existing.modified : undefined
  );
  if (existingModified === null) {
    return false;
  }
  const incomingModified = parseModified(
    typeof incoming.modified === 'string' ? incoming.modified : undefined
  );
  if (incomingModified === null) {
    return true;
  }
  return existingModified >= incomingModified;
};

export const mapFilteredAddressbooksToRows = (
  filteredValues: unknown,
  addressbooksMetaValues?: unknown
): AddressbookTableRow[] => {
  if (!filteredValues || typeof filteredValues !== 'object') {
    return [];
  }

  const rows = filteredValues as Record<
    string,
    AddressbookTableRow | undefined
  >;
  const metaValues = addressbooksMetaValues as
    | Record<string, Record<string, { contacts_count?: number }>>
    | null
    | undefined;

  return Object.keys(rows)
    .filter((k) => rows[k])
    .map((k) => {
      const value = rows[k];
      if (!value) {
        return {};
      }
      const key = rowKey(value, k);
      const meta = metaValues?.[String(value.accountId)]?.[String(value.id)];
      return {
        ...value,
        key,
        contacts_count: meta?.contacts_count || 0,
      };
    });
};

/**
 * Rebuild table rows from a filter snapshot without wiping in-progress edits.
 * Filter results are a one-shot get(), so a later meta refresh can otherwise
 * restore a stale party_id / Processed flag after a successful save.
 */
export const mergeAddressbookTableRows = (
  incomingRows: AddressbookTableRow[],
  existingRows: AddressbookTableRow[]
): AddressbookTableRow[] => {
  const existingByKey = new Map<string, AddressbookTableRow>();
  existingRows.forEach((row) => {
    if (row?.key) {
      existingByKey.set(String(row.key), row);
    }
  });

  return incomingRows.map((incoming) => {
    if (!incoming?.key) {
      return incoming;
    }
    const existing = existingByKey.get(String(incoming.key));
    if (!existing || !isLocalAddressbookRowNewer(existing, incoming)) {
      return incoming;
    }
    return {
      ...incoming,
      ...existing,
      contacts_count: incoming.contacts_count,
    };
  });
};

export const applyAddressbookContactsCount = (
  rows: AddressbookTableRow[],
  addressbooksMetaValues?: unknown
): AddressbookTableRow[] => {
  const metaValues = addressbooksMetaValues as
    | Record<string, Record<string, { contacts_count?: number }>>
    | null
    | undefined;
  return rows.map((row) => ({
    ...row,
    contacts_count:
      metaValues?.[String(row.accountId)]?.[String(row.id)]?.contacts_count ??
      row.contacts_count ??
      0,
  }));
};
